<template>
  <section class="user_management__content">
    <div v-if="!selectedUser">
      <header class="content__header">
        <h2 v-html="$t('userInfo.lnb.userManagement')"></h2>

        <div class="input__search">
          <input type="text"
                 class="search-input"
                 :value="searchKeyword"
                 @input="searchKeyword = $event.target.value.trim().toLowerCase()">
          <i class="icon icon--search"></i>
        </div>
      </header>

      <div v-if="hasResult"
           class="content__body" ref="scrollContent">
        <content-box>
          <table>
            <colgroup>
              <col width="158px">
              <col width="158px">
              <col width="208px">
              <col width="*">
              <col width="103px">
              <col width="103px">
            </colgroup>
            <thead class="item__header">
            <tr>
              <th class="align-left data__page">
                <sort-options
                  columnName="userId"
                  :sortColumn="sortColumn"
                  :isSortAsc="isSortAsc"
                  @onSortOptionChange="onSortOptionChange">{{ $t('common.id') }}</sort-options>
              </th>
              <th class="align-left data__page">
                <sort-options
                  columnName="branchName"
                  :sortColumn="sortColumn"
                  :isSortAsc="isSortAsc"
                  @onSortOptionChange="onSortOptionChange">{{ $t('common.corporation') }}</sort-options>
              </th>
              <th class="align-left data__page">
                <sort-options
                  columnName="email"
                  :sortColumn="sortColumn"
                  :isSortAsc="isSortAsc"
                  @onSortOptionChange="onSortOptionChange">{{ $t('common.email') }}</sort-options>
              </th>
              <th class="align-center ellipsis data__page">
                {{ $t('common.countryCode') }}
              </th>
              <th class="align-center data__page">
                <sort-options
                  columnName="pages"
                  :sortColumn="sortColumn"
                  :isSortAsc="isSortAsc"
                  @onSortOptionChange="onSortOptionChange"><span v-html="$t('common.pageRegistered')"></span></sort-options>
              </th>
              <th class="align-center data__page">
                <sort-options
                  columnName="updatePermission"
                  :sortColumn="sortColumn"
                  :isSortAsc="isSortAsc"
                  @onSortOptionChange="onSortOptionChange">{{ $t('common.role') }}</sort-options>
              </th>
            </tr>
            </thead>
            <tbody class="item__body">
            <tr v-for="user of userList"
                v-show="!user.isHide"
                @click="onSelectUser(user.userNum)">
              <td class="align-left ellipsis">
                <text-highlight :searchKeyword="searchKeyword"
                                :textItem="user.userId"></text-highlight>
              </td>
              <td class="align-left ellipsis">
                <text-highlight :searchKeyword="searchKeyword"
                                :textItem="user.branchName"></text-highlight>
              </td>
              <td class="align-left ellipsis">
                <text-highlight :searchKeyword="searchKeyword"
                                :textItem="user.email"></text-highlight>
              </td>
              <td class="align-center ellipsis">
                <text-highlight :searchKeyword="searchKeyword"
                                :textItem="user.originalSiteCodeList.join(', ')"
                                v-if="user.originalSiteCodeList"></text-highlight>
              </td>
              <td class="align-center ellipsis">
                <span v-if="user.pages > 0 || user.updatePermission === 'localManager'" v-html="user.pages"></span>
                <span v-else>-</span>
              </td>
              <td class="align-center">
                {{ $t('userInfo.requestManagement.approve.step1.' + user.updatePermission + '.title') }}
              </td>
            </tr>
            </tbody>
          </table>

          <div class="load" v-if="!userList">
            <ui-loading-circle>
              {{ $t('common.loading') }}
            </ui-loading-circle>
          </div>
        </content-box>

        <back-to-top :refName="'scrollContent'" />
      </div>

      <div v-else class="content__body">
        <div class="no-result">
          <i class="icon icon--empty"></i>
          <p class="title">{{ $t('dashboard.noApplies.searchResult.title') }}</p>
          <p>{{ $t('dashboard.noApplies.searchResult.desc') }}</p>
          <p class="keyword">'{{ searchKeyword }}'</p>
        </div>
      </div>
    </div>

    <user-detail v-else
      @onBack="onUserDetailBackButton"
      :selectedUser="selectedUser"></user-detail>

  </section>
</template>

<script>
import ContentBox from '@/components/ui/ContentBox'
import BackToTop from '@/components/ui/BackToTop'
import SortOptions from '@/components/ui/SortOptions'
import UserDetail from '@/components/user-info/user-management/UserManagementDetail'
import TextHighlight from '@/components/ui/TextHighlight'
import UiLoadingCircle from '@/components/ui/UiLoadingCircle.vue'
import { userList } from '@/api/user'

export default {
  components: {
    ContentBox,
    SortOptions,
    UserDetail,
    TextHighlight,
    UiLoadingCircle,
    BackToTop
  },
  data () {
    return {
      searchKeyword: '',
      hasResult: true,
      userList: null,
      sortOptions: {
        column: 'userId',
        isAsc: true
      },
      selectedUser: null
    }
  },
  computed: {
    sortColumn () {
      return this.sortOptions.column
    },
    isSortAsc () {
      return this.sortOptions.isAsc
    }
  },
  watch: {
    searchKeyword () {
      this.search()
    }
  },
  methods: {
    search () {
      if (!this.searchKeyword.length) {
        this.hasResult = true
      } else {
        this.hasResult = false
      }

      this.userList.forEach(item => {
        item.isHide = false
        if (this.searchKeyword.length) {
          if (!item.userId.toLowerCase().includes(this.searchKeyword) &&
            !(item.branchName && item.branchName.toLowerCase().includes(this.searchKeyword)) &&
            !(item.originalSiteCodeList && item.originalSiteCodeList.join(' ').includes(this.searchKeyword.toUpperCase())) &&
            !item.email.toLowerCase().includes(this.searchKeyword)
          ) {
            item.isHide = true
          } else {
            this.hasResult = true
          }
        }
      })
    },
    onSortOptionChange (columnName, isAsc) {
      this.userList.sort(function (a, b) {
        return String(a[columnName]).localeCompare(String(b[columnName]), undefined, { numeric: columnName === 'pages' })
      })
      if (!isAsc) {
        this.userList.reverse()
      }
      this.sortOptions = {
        column: columnName,
        isAsc: isAsc
      }
    },
    onUserDetailBackButton () {
      window.history.back()
      this.selectedUser = null
      this.load()
    },
    onSelectUser (userNum) {
      this.selectedUser = userNum
      window.location.href = '#' + userNum
    },
    load () {
      userList({
        active: true
      }).then(result => {
        this.userList = result
        this.userList.forEach(item => {
          if (item.updatePermission) {
            item.updatePermission = 'localManager'
          } else {
            item.updatePermission = 'viewer'
          }
          if (!item.pages && item.updatePermission === 'viewer') {
            item.pages = -1
          }
          if (item.originalSiteCodeList) {
            item.originalSiteCodeList.sort()
          }
        })
        this.search()
        this.onSortOptionChange(this.sortOptions.column, this.sortOptions.isAsc)
      })
    },
    onPopstate () {
      if (!window.location.hash) {
        this.selectedUser = null
        this.load()
      } else {
        this.selectedUser = window.location.hash.replace('#', '')
      }
    }
  },
  created () {
    this.load()
  },
  mounted () {
    window.addEventListener('hashchange', this.onPopstate)
  },
  destroyed () {
    window.removeEventListener('hashchange', this.onPopstate)
  }
}
</script>

<style lang="scss" scoped="scoped">
.user_management__content {
  position: relative;
  float: left;
  width: calc(100% - 211px);
  margin-top: 40px;
  box-sizing: border-box;

  .content__header {
    font-size: 26px;
    font-weight: bold;
    color: #444444;
    margin-left: 30px;
    margin-right: 30px;
    padding-bottom: 22px;

    h2 {
      padding-left: 10px;
    }

    .input__search {
      position: absolute;
      right: 30px;
      top: -5px;

      .search-input {
        width: 240px;
        height: 38px;
        line-height: 36px;
        border-radius: 20px;
        border: solid 1px #bbbbbb;
        background-color: #ffffff;
        padding-left: 18px;
        padding-right: 26px;
        box-sizing: border-box;
        font-size: 14px;
        color: #2f2f2f;

        &:focus {
          border: solid 1px #289eef;
        }
      }

      .icon {
        position: absolute;
        right: 11px;
        transform: translateY(-50%);
        top: 18px;
      }
    }
  }

  .content__body {
    height: calc(100vh - 136px) !important;
    width: calc(100% - 60px) !important;
    margin: 1px 30px 0;
    overflow-y: auto;
    padding: 1px;

    & /deep/ .content-box {
      padding: 25px;
    }

    .load {
      height: 440px;
      border-bottom: 1px solid #dddddd;
      position: relative;

      .loading {
        @include positionXYcenter;
      }
    }

    table {
      font-size: 13px;
      width: 100%;
      table-layout: fixed;
    }

    table thead tr {
      height: 50px;
      background-color: #555555;
      line-height: 1.23;
      color: #ffffff;
      text-align: center;

      th:nth-of-type(1) {
        position: relative;
        &:before {
          content: "";
          display: inline-block;
          width: 15px;
          height: 50px;
          position: absolute;
          top: 0;
          left: -5px;
          background-color: #555;
          border-radius: 5px;
        }
      }
      th:nth-last-of-type(1) {
        position: relative;
        &:after {
          content: "";
          display: inline-block;
          width: 10px;
          height: 50px;
          position: absolute;
          top: 0;
          right: -5px;
          background-color: #555;
          border-radius: 5px;
        }
      }

      .align-left {
        text-align: left;
        padding-left: 10px;
      }

      .align-right {
        text-align: right;
        padding-right: 2px;
      }

      th.head-weekly {
        span {
          vertical-align: middle;
        }

        .not-support-weekly {
          position: relative;
          display: inline-block;
          vertical-align: middle;
          margin-left: 5px;

          .icon--info-white-size17 {
            width: 13px;
            height: 13px;
            background-size: contain;
          }
        }
      }

      span {
        position: relative;
        display: inline-block;
        .icon {
          position: absolute;
          top: 50%;
          right: -19px;
          transform: translateY(-50%);
        }
      }
    }

    table tbody tr {
      height: 50px;
      color: #2f2f2f;
      line-height: 1.23;
      font-size: 13px;
      text-align: center;
      border-bottom: 1px solid #ebebeb;
      box-sizing: border-box;
      cursor: pointer;

      &:not(.deleted):hover {
        background-color: #f4f4f4;
      }

      &.checked:not(.deleted) {
        border: solid 1px #289eef;
        outline: solid 3px rgba(40, 158, 239, 0.18);
      }

      .align-left {
        text-align: left;
        padding-left: 10px;
        box-sizing: border-box;
      }
      .align-right {
        text-align: right;
        padding-right: 15px;
        box-sizing: border-box;
      }

      td {
        padding: 10px 0;
      }

      .data__button {
        .ui-btn {
          margin-left: 5px;
        }
      }
    }
  }

  .no-result {
    @include positionYcenter;
    text-align: center;
    font-size: 14px;
    color: #767676;
    width: 100%;

    .title {
      margin: 15px 0;
      font-size: 20px;
      font-weight: 500;
      color: #2f2f2f;
    }
    .keyword {
      font-size: 15px;
      line-height: 24px;
      font-weight: 500;
      color: #ff8400;
      margin: 30px auto 0;
      width: 524px;
      word-break: break-all;
    }
  }
}
</style>
